import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import {
  DateInput,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  RefreshIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { syncAccCurrencyRatesOnline } from '../../../services/mutations';
import { accountingReturnState } from '../shared/navigation';
import { moduleConfig } from './config';
import { formatCurrencyCodeSymbol, listRateCurrencies } from './currencyDisplay';
import { useAccCurrencyRates, type AccCurrencyRate } from './useData';

type Currency = { id: number; code: string; name: string; symbol?: string | null; isActive?: boolean };

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatRate = (value: number | string | null | undefined) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

export const List: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const confirm = useConfirm();
  const { can } = useAuth();
  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'acc_currency_rates';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const currencyIdFromUrl = searchParams.get('currencyId');
  const initialCurrencyId =
    currencyIdFromUrl && Number.isFinite(Number(currencyIdFromUrl)) ? Number(currencyIdFromUrl) : null;

  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(initialCurrencyId);
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [syncDate, setSyncDate] = useState(todayIso);
  const [syncingOnline, setSyncingOnline] = useState(false);
  const [syncingAllOnline, setSyncingAllOnline] = useState(false);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'rateDate', direction: 'desc' },
    {
      defaultDirectionForKey: (key) => (key === 'rateDate' || key === 'createdAt' ? 'desc' : 'asc'),
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data: currenciesRaw, isLoading: currenciesLoading } = useSWR<Currency[] | { data: Currency[] }>(
    '/currencies',
    fetcher,
  );

  const currencies = useMemo(
    () =>
      listRateCurrencies(
        (Array.isArray(currenciesRaw) ? currenciesRaw : currenciesRaw?.data ?? [])
          .slice()
          .sort((a, b) => a.code.localeCompare(b.code)),
      ),
    [currenciesRaw],
  );

  const filteredCurrencies = useMemo(() => {
    const q = currencyFilter.trim().toLowerCase();
    if (!q) return currencies;
    return currencies.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.symbol?.trim().toLowerCase().includes(q) ?? false),
    );
  }, [currencies, currencyFilter]);

  useEffect(() => {
    if (currencies.length === 0) {
      if (selectedCurrencyId != null) setSelectedCurrencyId(null);
      return;
    }
    const valid =
      selectedCurrencyId != null && currencies.some((c) => c.id === selectedCurrencyId);
    if (!valid) {
      setSelectedCurrencyId(currencies[0].id);
    }
  }, [currencies, selectedCurrencyId]);

  const activeCurrencyId = useMemo(() => {
    if (selectedCurrencyId != null && currencies.some((c) => c.id === selectedCurrencyId)) {
      return selectedCurrencyId;
    }
    return currencies[0]?.id ?? null;
  }, [currencies, selectedCurrencyId]);

  const selectedCurrency = useMemo(
    () => (activeCurrencyId != null ? currencies.find((c) => c.id === activeCurrencyId) ?? null : null),
    [currencies, activeCurrencyId],
  );

  const ratesEnabled = activeCurrencyId != null;

  const { data, total, isLoading, mutate } = useAccCurrencyRates(
    ratesEnabled ? search || undefined : undefined,
    page,
    itemsPerPage,
    ratesEnabled
      ? {
          currencyId: activeCurrencyId,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }
      : undefined,
  );

  useEffect(() => {
    setPage(1);
  }, [search, activeCurrencyId, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    const current = searchParams.get('currencyId');
    const nextValue = activeCurrencyId != null ? String(activeCurrencyId) : null;
    if (current === nextValue || (current == null && nextValue == null)) return;
    const next = new URLSearchParams(searchParams);
    if (nextValue != null) {
      next.set('currencyId', nextValue);
    } else {
      next.delete('currencyId');
    }
    setSearchParams(next, { replace: true });
  }, [activeCurrencyId, searchParams, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));
  const formatDate = moduleConfig.utils.formatDate;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const summarizeSyncResult = (result: Awaited<ReturnType<typeof syncAccCurrencyRatesOnline>>) => {
    const parts: string[] = [];
    if (result.created > 0) parts.push(`${result.created} created`);
    if (result.updated > 0) parts.push(`${result.updated} updated`);
    if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
    if (result.failed > 0) parts.push(`${result.failed} failed`);
    return parts.length > 0 ? parts.join(', ') : 'No changes';
  };

  const handleSyncOnline = async (scope: 'selected' | 'all') => {
    if (!syncDate) {
      showError('Select a sync date.');
      return;
    }
    if (scope === 'selected' && activeCurrencyId == null) {
      showError('Select a currency first.');
      return;
    }

    const setLoading = scope === 'selected' ? setSyncingOnline : setSyncingAllOnline;
    setLoading(true);
    try {
      const result = await syncAccCurrencyRatesOnline({
        rateDate: syncDate,
        ...(scope === 'selected' && activeCurrencyId != null ? { currencyIds: [activeCurrencyId] } : {}),
        overwrite: true,
      });
      await mutate();
      showSuccess(`Online sync for ${formatDateGlobal(syncDate)}: ${summarizeSyncResult(result)}.`);
      if (result.failed > 0) {
        const failed = result.results.filter((r) => r.status === 'failed');
        const preview = failed.slice(0, 3).map((r) => `${r.currencyCode}: ${r.message ?? 'failed'}`).join('; ');
        showError(preview || 'Some currencies could not be synced.');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error)?.message
        ?? 'Could not sync rates online.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const newRateHref =
    activeCurrencyId != null
      ? `${moduleConfig.module.route.new}?currencyId=${activeCurrencyId}`
      : moduleConfig.module.route.new;

  const handleDelete = async (e: React.MouseEvent, row: AccCurrencyRate) => {
    e.stopPropagation();
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: moduleConfig.delete.message,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await moduleConfig.delete.handler(row.id);
      await mutate();
      showSuccess('Currency rate deleted.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? moduleConfig.delete.errorMessage;
      showError(message);
    }
  };

  const clearCurrencyFilter = () => setCurrencyFilter('');
  const clearRateSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const returnState = accountingReturnState(location.pathname, location.search);

  return (
    <div className="flex h-full min-h-0 gap-1 p-1">
      <div className="w-80 shrink-0 min-h-0">
        <GeneralList
          className="h-full"
          title="Currencies"
          description={`${currencies.length} foreign`}
          density="compact"
          search={{
            value: currencyFilter,
            onChange: setCurrencyFilter,
            placeholder: 'Filter by code or name…',
          }}
          hasFilters={Boolean(currencyFilter.trim())}
          onClearFilters={clearCurrencyFilter}
          isLoading={currenciesLoading}
          loadingLabel="Loading currencies…"
          isEmpty={!currenciesLoading && filteredCurrencies.length === 0}
          emptyState={{
            icon: <DocumentTextIcon className="h-10 w-10" />,
            title: currencyFilter.trim() ? 'No currencies match' : 'No foreign currencies',
            hint: currencyFilter.trim() ? 'Try a different filter.' : 'Add an active non-USD currency first.',
            action: currencyFilter.trim() ? (
              <button
                type="button"
                onClick={clearCurrencyFilter}
                className={cn(
                  'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                Clear filter
              </button>
            ) : undefined,
          }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                <th className={cn('sticky top-0 z-10 font-medium', colors.surface)}>Currency</th>
                <th className={cn('sticky top-0 z-10 font-medium', colors.surface)}>Name</th>
              </tr>
            </thead>
            <tbody>
              {filteredCurrencies.map((currency) => {
                const active = activeCurrencyId === currency.id;
                return (
                  <tr
                    key={currency.id}
                    onClick={() => setSelectedCurrencyId(currency.id)}
                    className={cn(
                      'cursor-pointer border-b transition-colors last:border-b-0',
                      colors.border,
                      active
                        ? cn(colors.primary, colors.primaryText)
                        : cn(colors.text, colors.interactiveHover),
                    )}
                  >
                    <td className="font-medium tabular-nums whitespace-nowrap">
                      {formatCurrencyCodeSymbol(currency)}
                    </td>
                    <td className={cn('truncate', active ? 'text-white/80' : colors.textMuted)}>
                      {currency.name}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </GeneralList>
      </div>

      <div className="min-h-0 min-w-0 flex-1">
        <GeneralList
          className="h-full"
          title={selectedCurrency ? formatCurrencyCodeSymbol(selectedCurrency) : 'Rates'}
          description={
            selectedCurrency
              ? `${isLoading ? '…' : total ?? 0} rate${total === 1 ? '' : 's'} · ${selectedCurrency.name}`
              : 'Select a currency'
          }
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: moduleConfig.search.placeholder,
          }}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <DateInput
                value={syncDate}
                onChange={(e) => setSyncDate(e.target.value)}
                showFormatHint={false}
                className="w-[9.5rem]"
              />
              <button
                type="button"
                disabled={syncingOnline || syncingAllOnline || activeCurrencyId == null}
                onClick={() => handleSyncOnline('selected')}
                title="Fetch and save online rate for selected currency"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium disabled:opacity-50',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                <RefreshIcon className={cn('h-4 w-4', syncingOnline && 'animate-spin')} />
                Fetch
              </button>
              <button
                type="button"
                disabled={syncingOnline || syncingAllOnline || currencies.length === 0}
                onClick={() => handleSyncOnline('all')}
                title="Fetch and save online rates for all currencies"
                className={cn(
                  'hidden md:inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium disabled:opacity-50',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                <RefreshIcon className={cn('h-4 w-4', syncingAllOnline && 'animate-spin')} />
                Sync all
              </button>
              {canEdit && ratesEnabled ? (
                <Link
                  to={newRateHref}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                    colors.primary,
                  )}
                >
                  <PlusIcon className="h-4 w-4" />
                  New rate
                </Link>
              ) : null}
            </div>
          }
          hasFilters={Boolean(search.trim())}
          onClearFilters={clearRateSearch}
          sorting={{ sortConfig, onSort: handleSort }}
          isLoading={ratesEnabled && isLoading}
          loadingLabel="Loading rates…"
          isEmpty={!isLoading && (!ratesEnabled || data.length === 0)}
          emptyState={{
            icon: <DocumentTextIcon className="h-12 w-12" />,
            title: !ratesEnabled
              ? 'Select a currency'
              : search.trim()
                ? moduleConfig.emptyStates.noSearchResults
                : 'No rates yet',
            hint: !ratesEnabled
              ? 'Choose a currency on the left to view rates.'
              : search.trim()
                ? 'Try adjusting the search.'
                : moduleConfig.emptyStates.noData,
            action: !ratesEnabled ? undefined : search.trim() ? (
              <button
                type="button"
                onClick={clearRateSearch}
                className={cn(
                  'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                Clear search
              </button>
            ) : canEdit ? (
              <Link
                to={newRateHref}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                  colors.primary,
                )}
              >
                <PlusIcon className="h-4 w-4" />
                New rate
              </Link>
            ) : undefined,
          }}
          pagination={
            ratesEnabled
              ? {
                  page,
                  totalPages,
                  total: total ?? 0,
                  itemsPerPage,
                  pageSizeOptions,
                  onPageChange: setPage,
                  onItemsPerPageChange: (size) => {
                    setItemsPerPage(size);
                    setPage(1);
                  },
                }
              : undefined
          }
        >
          <table className="w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
                <GeneralListSortableTh sortKey="rateDate">Rate date</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="rate" align="right">
                  Rate (USD)
                </GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.id}
                  onClick={() =>
                    navigate(moduleConfig.module.route.detail(row.id), { state: returnState })
                  }
                  className={cn(
                    'cursor-pointer border-b transition-colors last:border-b-0',
                    colors.border,
                    colors.text,
                    colors.interactiveHover,
                  )}
                >
                  <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5">
                      <Link
                        to={moduleConfig.module.route.detail(row.id)}
                        state={returnState}
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="View"
                        aria-label="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                      {canEdit ? (
                        <Link
                          to={moduleConfig.module.route.edit(row.id)}
                          state={returnState}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            colors.interactiveHover,
                          )}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, row)}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            'hover:text-red-600',
                          )}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="pr-6 whitespace-nowrap">{formatDate(row.rateDate?.slice(0, 10))}</td>
                  <td className={cn('pr-6 whitespace-nowrap text-right tabular-nums', colors.textSecondary)}>
                    {formatRate(row.rate)}
                  </td>
                  <td className="w-full" aria-hidden />
                </tr>
              ))}
            </tbody>
          </table>
        </GeneralList>
      </div>
    </div>
  );
};
